import { landingApi } from 'Pages/landings/newLandings/common/api'
import {
	OrderCreateParams,
	OrderModel,
	ReceivingFileModel,
	LeadCreate,
} from 'Pages/landings/newLandings/common/types'
import {
	BEARER_TYPE,
	ORG_TYPE,
	urls,
	ProductNames,
} from 'Pages/landings/newLandings/common/constants'
import TimesNewRoman from 'Assets/fonts/Times_New_Roman.ttf'
import type { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { sanitizeHtml } from 'Helpers'
import { FrontUrls } from 'Constants/FrontUrls'

export const getQuery = () => {
	const { location } = window
	const params = location.search
		.substr(1)
		.split('&')
		.map((el) => el.split('='))
	return params.reduce((acc, [key, value]) => {
		acc[key] = value ? decodeURIComponent(value) : value
		return acc
	}, {} as Record<string, string>)
}

export const formatDate = (date: Date) =>
	date.toLocaleDateString('ru-RU', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	})

export const formatDateForBackend = (date: string) =>
	new Date(date).toLocaleDateString('sv-SE', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	})

export const getParamsFromUrl = () => {
	const params = getQuery()
	return {
		url: window.location.href,
		source: params.source,
		utm_medium: params.utm_medium,
		utm_campaign: params.utm_campaign,
		utm_content: params.utm_content,
	}
}

export const concatNames = <T extends { id: string | number; name: string }>(
	data: T[] = []
): string => {
	return data.reduce(
		(acc: string, item: T): string =>
			acc && acc.length ? `${acc},  ${item.name} ` : `${item.name}`,
		''
	)
}

export const normalizePhone = (value) => {
	if (!value) return value
	if (value.length === 1) {
		return `+7${value.replace(/[^\d]/g, '')}`
	}
	if (value === '+7') {
		return null
	}
	const onlyNums = value.replace(/[^\d]/g, '')
	return `+${onlyNums}`
}

export const submitLeadAndOrder = (
	parent_id: number | null,
	paramsOrder: OrderCreateParams,
	token: string,
	regionName: string | undefined,
	callback: (res: OrderModel | LeadCreate) => void,
	errorCallback: (res: Record<string, string>) => void,
	file?: ReceivingFileModel[],
	needConfirmPhone?: boolean
) => {
	const { createOrder, postLeadCreate } = landingApi
	let org_type = paramsOrder?.inn?.length === 10 ? ORG_TYPE.OOO : paramsOrder?.inn
	if (paramsOrder?.inn?.length === 12) org_type = ORG_TYPE.IP
	const timezone = -(new Date().getTimezoneOffset() / 60)
	const currentTimezone = timezone > 0 ? `+${String(timezone)}` : String(timezone)
	const postLead: LeadCreate = {
		org_type,
		parent_id,
		parent_type: 'SberMq::ExternalForm',
		bearer: needConfirmPhone ? BEARER_TYPE.SMS : BEARER_TYPE.DIGITAL,
		empty_middle_name: true,
		phone: paramsOrder?.phone,
		inn: paramsOrder?.inn,
		first_name: paramsOrder?.first_name ?? '',
		timezone: currentTimezone,
		area_name: regionName ?? '',
	}
	if (window.location.href.includes(urls.landings.notary)) {
		postLead.file = file
		postLead.parent_type = 'SberMq::ExternalFormNotary'
	}
	postLeadCreate(postLead)
		.then((response) => {
			if (needConfirmPhone) {
				callback(response)
			} else if (response)
				return createOrder(paramsOrder, token).then((response) => callback(response))
		})
		.catch((error) => errorCallback(error.data.errors))
}

export const submitOrder = (
	parent_id: number | null,
	paramsOrder: OrderCreateParams,
	token: string,
	regionName: string | undefined,
	callback: (res: OrderModel | LeadCreate) => void,
	errorCallback: (res: Record<string, string>) => void,
	file?: ReceivingFileModel[],
	needConfirmPhone?: boolean
) => {
	const { createOrder } = landingApi
	let org_type = paramsOrder?.inn?.length === 10 ? ORG_TYPE.OOO : paramsOrder?.inn
	if (paramsOrder?.inn?.length === 12) org_type = ORG_TYPE.IP
	const timezone = -(new Date().getTimezoneOffset() / 60)
	const currentTimezone = timezone > 0 ? `+${String(timezone)}` : String(timezone)
	const postLead: LeadCreate = {
		org_type,
		parent_id,
		parent_type: 'SberMq::ExternalForm',
		bearer: needConfirmPhone ? BEARER_TYPE.SMS : BEARER_TYPE.DIGITAL,
		empty_middle_name: true,
		phone: paramsOrder?.phone,
		inn: paramsOrder?.inn,
		first_name: paramsOrder?.first_name ?? '',
		last_name: paramsOrder?.last_name ?? '',
		timezone: currentTimezone,
		area_name: regionName ?? '',
	}
	if (window.location.href.includes(urls.landings.notary)) {
		postLead.file = file
		postLead.parent_type = 'SberMq::ExternalFormNotary'
	}
	createOrder(paramsOrder, token)
		.then((response) => callback(response))
		.catch((error) => errorCallback(error.data.errors))
}

export const followingLink = (url) => {
	window.location.href = url
}

const prepareHtml = (element: Element): string => {
	const wrapper = document.createElement('div')
	const styleContent = `#container * { font-family: TimesNewRoman} 
		#container .tc-cell {font-size: 11px !important; margin-bottom: 5px;}`
	const style = document.createElement('style')

	style.textContent = styleContent
	wrapper.setAttribute('style', 'font-size:11px; padding: 10px 10px; width:580px;')

	const node = element.cloneNode(true) as HTMLElement

	node.setAttribute('id', 'container')
	wrapper.prepend(style)
	wrapper.append(node)

	return wrapper.outerHTML
}

interface Options {
	docName?: string
}
export async function htmlToPdfAndSave(element: Element, options?: Options): Promise<void> {
	const { docName = 'document' } = options ?? {}
	const { default: JSPDF } = await import('jspdf')

	return new Promise((res) => {
		const html = prepareHtml(element)

		const doc = new JSPDF({
			orientation: 'p',
			unit: 'pt',
			format: 'a4',
		})
		const font = TimesNewRoman.split(',')[1]
		doc.addFileToVFS('TimesNewRoman.ttf', font)
		doc.addFont('TimesNewRoman.ttf', 'TimesNewRoman', 'normal')
		doc.setFont('TimesNewRoman')

		doc.html(html, {
			callback: async (pdf: jsPDF) => {
				await pdf.save(`${docName}.pdf`, { returnPromise: true })
				res()
			},
			autoPaging: 'text',
			margin: [12, 8, 15, 8],
		})
	})
}

const getType = (fileName: string): string => fileName.split('.')?.pop().toLowerCase()
export const canShowFile = (fileName: string): boolean => {
	switch (getType(fileName)) {
		case 'jpg':
		case 'jpeg':
		case 'pdf':
		case 'png':
			return true
		default:
			return false
	}
}

export const downloadFile = (url: string, title: string): void => {
	const link = document.createElement('a')
	link.href = url
	link.setAttribute('download', title)
	link.click()
}

export const isImageFile = (fileName: string): boolean => {
	if (!fileName) {
		return null
	}
	switch (getType(fileName)) {
		case 'jpg':
		case 'jpeg':
		case 'png':
			return true
		default:
			return false
	}
}

export const addUrlParams = (product: string, erid: string) => {
	const currentHref = window.location.href
	const param = currentHref.includes('?') ? '&' : '?'
	if (window.location.pathname === FrontUrls.landing.multi) {
		window.location.href =
			product === ProductNames.RBO
				? `${currentHref.replace('/multi', '/products')}${param}product=${product}&erid=${erid}`
				: `${currentHref}${param}partner=${product}&erid=${erid}`
	} else {
		window.location.href = `${currentHref}${param}product=${product}&erid=${erid}`
	}
}

export function debounce<T>(f, interval) {
	let timer = null

	return (...args) => {
		clearTimeout(timer)
		return new Promise<T>((resolve) => {
			timer = setTimeout(() => resolve(f(...args)), interval)
		})
	}
}

const keysBodySanitize = ['time', 'id', 'type', 'withHeadings', 'divider', 'style', 'version']

export const reckSanitize = (body) => {
	const ans = body
	Object.keys(body).forEach((key) => {
		if (!keysBodySanitize.includes(key)) {
			if (typeof body[key] === 'string') {
				ans[key] = sanitizeHtml(ans[key])
			} else {
				ans[key] = reckSanitize(ans[key])
			}
		}
	})
	return ans
}
